import { css } from '@eti/styles';
import { breakpoints, vars, Stack, Inline, Checkbox } from '@etg/wings';
import { price as priceUtils, usePrevious } from '@eti/utils';
import { Notification, ScreenReaderPrice, variants } from '@eti/components';
import { AnimatePresence, m as motion } from 'framer-motion';
import { ConnectedComboBox } from '@eti/form';
import { useEffect, useRef, useState } from 'react';
import TravelerDescription from '../../common/components/TravelerDescription';
import { visaTypes } from '../utils/constants';
import SimpleVisaTypeDescription from '../components/SimpleVisaTypeDescription';
import { useProductCardContext } from '../../common/context/ProductCardContext';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const animaitonHidden = css`
  height: 0;
  overflow: hidden;
`;
const dropdownStyles = css`
  width: 312px;

  & > span {
    height: auto;
    margin-top: 0;
  }
`;
const checkboxLabelAndPriceStyles = css`
  display: flex;
  justify-content: space-between;
  padding-inline-start: 0.25rem;
`;
const wrapperStyles = css`
  border-top: ${vars.colors.divider} 1px solid;
  padding: 16px;

  @media (min-width: ${breakpoints._768}) {
    padding-block: 10px;
    padding-inline: 0 16px;
  }

  &:last-child {
    border-bottom: ${vars.colors.divider} 1px solid;
  }
`;
const travelerDescriptionWrapperStyles = css`
  align-items: center;
  display: flex;
`;
const priceStyles = css`
  font-size: 1rem;
  font-weight: 700;
`;
const descriptionsWrapperStyles = css`
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
  gap: 4px;
`;
const TravelerSelection = ({
  traveler,
  productName,
  isAnimationComplete
}) => {
  const {
    choice,
    ageType,
    id,
    name,
    nationalities,
    isSelected
  } = traveler;
  const {
    noVisaInfo,
    visaInfoHeader,
    nationalityPlaceholder,
    nationalityLabel,
    selectTraveler
  } = useProductCardContext().extraProps;
  const usePreviousSelection = usePrevious(isSelected);
  const {
    descriptions = [],
    value,
    priceRaw
  } = choice;
  const disabled = Boolean(!value) || priceRaw <= 0;
  const formattedPrice = priceUtils.formatPrice(priceRaw);
  const formatChoice = visaTraveler => {
    var _visaTraveler$choice;
    return (_visaTraveler$choice = visaTraveler === null || visaTraveler === void 0 ? void 0 : visaTraveler.choice) !== null && _visaTraveler$choice !== void 0 ? _visaTraveler$choice : '';
  };
  const normalizeChoice = selectedNationality => {
    const visaTravelerInfo = {
      choice: selectedNationality,
      selected: Boolean(selectedNationality.priceRaw > 0)
    };
    return visaTravelerInfo;
  };
  const displayWarning = (visas = []) => visas.some(({
    type
  }) => type !== visaTypes.VISA_NOT_NEEDED);
  const shouldDisplayWarning = isAnimationComplete && !isSelected && displayWarning(descriptions);
  const warningRef = useRef(null);
  const [shouldFocus, setShouldFocus] = useState(false);
  useEffect(() => {
    if (shouldDisplayWarning && warningRef.current && shouldFocus) {
      var _warningRef$current;
      (_warningRef$current = warningRef.current) === null || _warningRef$current === void 0 || _warningRef$current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      setTimeout(() => {
        var _warningRef$current2;
        (_warningRef$current2 = warningRef.current) === null || _warningRef$current2 === void 0 || _warningRef$current2.focus();
      }, 400);
    }
  }, [shouldDisplayWarning, shouldFocus]);
  return /*#__PURE__*/_jsxs(Stack, {
    className: wrapperStyles,
    "data-testid": `${productName}-traveler-${id}`,
    spacing: 16,
    children: [/*#__PURE__*/_jsxs("div", {
      className: checkboxLabelAndPriceStyles,
      children: [/*#__PURE__*/_jsxs(Inline, {
        alignY: "center",
        spacing: 0,
        children: [/*#__PURE__*/_jsx(motion.div, {
          animate: {
            width: disabled ? 0 : 'auto',
            marginInlineEnd: disabled ? 0 : '8px'
          },
          style: {
            overflow: 'hidden'
          },
          transition: {
            type: 'tween'
          },
          children: /*#__PURE__*/_jsx(Checkbox, {
            "aria-hidden": disabled,
            checked: isSelected || false,
            "data-testid": `simpleVisaCheckboxTraveler${id}`,
            id: `simpleVisaCheckboxTraveler${id}`,
            onChange: () => selectTraveler(id, !isSelected)
          })
        }), /*#__PURE__*/_jsx(Checkbox.Label, {
          htmlFor: `simpleVisaCheckboxTraveler${id}`,
          children: /*#__PURE__*/_jsx(TravelerDescription, {
            className: travelerDescriptionWrapperStyles,
            name: name,
            type: ageType
          })
        })]
      }), /*#__PURE__*/_jsx("p", {
        "aria-hidden": true,
        className: priceStyles,
        "data-testid": `${productName}-traveler-${id}.price`,
        children: formattedPrice
      }), /*#__PURE__*/_jsx(ScreenReaderPrice, {
        price: formattedPrice
      })]
    }), /*#__PURE__*/_jsx(ConnectedComboBox, {
      className: dropdownStyles,
      "data-testid": `${productName}-nationality-${id}`,
      format: formatChoice,
      label: nationalityLabel,
      name: `simpleVisa.travelers.traveler-${id}`,
      normalize: normalizeChoice,
      onBlur: e => {
        // if selection goes from false to false, it should not trigger onBlur otherwise combobox selects the first element
        if (!usePreviousSelection && !isSelected) {
          e.preventDefault();
        }
      },
      onChange: () => setShouldFocus(true),
      options: nationalities,
      placeholder: nationalityPlaceholder
    }), shouldDisplayWarning && /*#__PURE__*/_jsx(motion.div, {
      animate: {
        height: shouldDisplayWarning ? 'auto' : 0,
        opacity: shouldDisplayWarning ? 1 : 0,
        marginTop: shouldDisplayWarning ? 0 : '-16px'
      },
      className: animaitonHidden,
      transition: {
        type: 'tween'
      },
      children: /*#__PURE__*/_jsx(Notification, {
        dataTestId: "visa-type-notification",
        ref: warningRef,
        title: visaInfoHeader,
        variant: variants.WARNING,
        children: noVisaInfo
      })
    }), /*#__PURE__*/_jsx(AnimatePresence, {
      children: descriptions && /*#__PURE__*/_jsx(motion.div, {
        animate: {
          height: 'auto',
          marginTop: 0
        },
        className: animaitonHidden,
        exit: {
          height: 0,
          marginTop: '-16px'
        },
        initial: {
          height: 0,
          marginTop: '-16px'
        },
        transition: {
          type: 'tween'
        },
        children: /*#__PURE__*/_jsx("div", {
          className: descriptionsWrapperStyles,
          children: descriptions.map(({
            text,
            type
          }, index) => /*#__PURE__*/_jsx(SimpleVisaTypeDescription, {
            text: text,
            type: type
          }, index))
        })
      })
    })]
  }, name);
};
export default TravelerSelection;
//# sourceMappingURL=TravelerSelection.js.map